import { useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { PrivacyFurnitureItem, PrivacyRoomItem, PrivacyLevel } from "../scenario/types";

const DEG = Math.PI / 180;
/** Shared desk-top / shelf heights so furniture and the items resting on
 *  them agree without every item needing to repeat the number. */
const DESK_H = 0.74;
const SHELF_H = 1.05;

/** Screen-space pin (percent of viewport) for one clickable item, keyed by id. */
export type PrivacyItemPin = { id: string; xPct: number; yPct: number; visible: boolean; distance: number };
/** Screen-space pin for the laptop's Episode-10 "click to open" sticky note. */
export type LaptopStickyPin = { xPct: number; yPct: number; visible: boolean; distance: number };

const LEVEL_COLOR: Record<PrivacyLevel, string> = {
  public: "#4a9e78",
  internal: "#3f7fc4",
  confidential: "#c98a2e",
  restricted: "#c94f4f",
};

/* ---------------------------------------------------------------------- *
 * Canvas textures — one drawing helper per prop "look". Kept as flat 2D
 * canvases (the established pattern in this codebase, see DeskPackage's
 * sticky/label textures) rather than hand-modelled geometry for the
 * printed/on-screen CONTENT; the physical shape (paper lying flat, laptop
 * body, printer body, passport booklet) is real 3D geometry below.
 * ---------------------------------------------------------------------- */

/** A real sheet of paper / folder lying flat on a desk, viewed top-down.
 *  Portrait "document" look: a thin colour band (sensitivity accent),
 *  ruled placeholder lines, a big glyph, and the label — plus a small
 *  green check once found. */
function makePaperTexture(item: PrivacyRoomItem, found: boolean): THREE.CanvasTexture {
  const w = 340;
  const h = 440;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  const color = LEVEL_COLOR[item.level];
  g.fillStyle = item.folder ? "#d8c496" : "#f5f2e9";
  g.fillRect(0, 0, w, h);
  if (item.folder) {
    // manila folder tab, top edge
    g.fillStyle = "#c7b077";
    g.fillRect(w * 0.32, 0, w * 0.36, 26);
  }
  g.fillStyle = color;
  g.fillRect(0, item.folder ? 26 : 0, w, 10);
  g.fillStyle = "rgba(30,26,18,0.14)";
  const lineStart = item.folder ? 90 : 70;
  for (let i = 0; i < 6; i++) {
    g.fillRect(30, lineStart + i * 34, w - 60, 8);
  }
  g.textAlign = "center";
  g.font = "76px 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif";
  g.fillStyle = "#20242a";
  g.fillText(item.icon, w / 2, 200);
  g.font = "700 21px Segoe UI, Arial, sans-serif";
  g.fillStyle = "#2a2f35";
  wrapCentered(g, item.label, w / 2, h - 70, w - 48, 25);
  if (found) drawCheck(g, w - 46, 46, 30, color);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** Small yellow Post-it — same visual family as DeskPackage's "Click to
 *  open" note, but with the item's own short text instead. */
function makeStickyTexture(item: PrivacyRoomItem, found: boolean): THREE.CanvasTexture {
  const s = 300;
  const cv = document.createElement("canvas");
  cv.width = s;
  cv.height = s;
  const g = cv.getContext("2d")!;
  g.fillStyle = "#ffe873";
  g.fillRect(0, 0, s, s);
  g.fillStyle = "rgba(0,0,0,0.05)";
  g.fillRect(0, 0, s, 8);
  g.font = "56px 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif";
  g.textAlign = "center";
  g.fillText(item.icon, s / 2, 110);
  g.fillStyle = "#3a2e0c";
  g.font = "700 26px Comic Sans MS, Segoe Script, cursive";
  wrapCentered(g, item.label, s / 2, 170, s - 30, 28);
  if (found) drawCheck(g, s - 38, 34, 22, "#2ea36f");
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** Printout mid-eject: a chart + title, like a page fresh off the printer. */
function makePrintoutTexture(item: PrivacyRoomItem, found: boolean): THREE.CanvasTexture {
  const w = 340;
  const h = 440;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  g.fillStyle = "#fbfaf6";
  g.fillRect(0, 0, w, h);
  g.fillStyle = LEVEL_COLOR[item.level];
  g.fillRect(0, 0, w, 8);
  g.textAlign = "left";
  g.fillStyle = "#20242a";
  g.font = "700 22px Segoe UI, Arial, sans-serif";
  g.fillText("Internal Salary", 26, 54);
  g.fillText("Comparison", 26, 82);
  g.strokeStyle = "rgba(0,0,0,0.15)";
  g.lineWidth = 1.5;
  g.strokeRect(24, 100, w - 48, 190);
  // simple bar chart
  const bars = [0.42, 0.68, 0.5, 0.82, 0.6, 0.3];
  const bw = (w - 48 - 40) / bars.length;
  bars.forEach((v, i) => {
    const bh = 150 * v;
    g.fillStyle = i % 2 === 0 ? "#4a9e78" : "#3f7fc4";
    g.fillRect(24 + 20 + i * bw, 270 - bh, bw - 8, bh);
  });
  g.strokeStyle = "rgba(0,0,0,0.25)";
  g.beginPath();
  g.moveTo(24, 270);
  g.lineTo(w - 24, 270);
  g.stroke();
  g.font = "15px Segoe UI, Arial, sans-serif";
  g.fillStyle = "#555";
  g.fillText("By department \u2014 confidential", 26, 316);
  g.font = "13px monospace";
  for (let i = 0; i < 5; i++) g.fillRect(26, 336 + i * 16, w - 90, 5);
  if (found) drawCheck(g, w - 46, 46, 30, LEVEL_COLOR[item.level]);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** Closed passport booklet cover, viewed top-down. */
function makePassportTexture(found: boolean): THREE.CanvasTexture {
  const s = 260;
  const cv = document.createElement("canvas");
  cv.width = s;
  cv.height = s * 1.42;
  const g = cv.getContext("2d")!;
  g.fillStyle = "#2c2f6b";
  g.fillRect(0, 0, cv.width, cv.height);
  g.strokeStyle = "rgba(255,215,120,0.85)";
  g.lineWidth = 3;
  g.strokeRect(14, 14, cv.width - 28, cv.height - 28);
  g.fillStyle = "rgba(255,215,120,0.9)";
  g.beginPath();
  g.arc(cv.width / 2, cv.height * 0.4, 34, 0, Math.PI * 2);
  g.fill();
  g.font = "26px serif";
  g.textAlign = "center";
  g.fillStyle = "#2c2f6b";
  g.fillText("\u{1F6C2}", cv.width / 2, cv.height * 0.4 + 10);
  g.fillStyle = "rgba(255,225,150,0.95)";
  g.font = "700 18px Georgia, 'Times New Roman', serif";
  g.textAlign = "center";
  g.fillText("PASSPORT", cv.width / 2, cv.height * 0.7);
  if (found) drawCheck(g, cv.width - 34, 34, 22, "#e0a840");
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** Laptop screen: two stacked "app window" previews (social post + email),
 *  drawn once on the shared furniture prop so both items visibly live on
 *  the SAME machine instead of floating cards. */
function makeLaptopScreenTexture(): THREE.CanvasTexture {
  const w = 512;
  const h = 320;
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const g = cv.getContext("2d")!;
  g.fillStyle = "#e7ebee";
  g.fillRect(0, 0, w, h);
  // top: social post window
  g.fillStyle = "#ffffff";
  g.fillRect(14, 12, w - 28, 132);
  g.fillStyle = "#3f7fc4";
  g.fillRect(14, 12, w - 28, 26);
  g.fillStyle = "#fff";
  g.font = "700 14px Segoe UI, Arial, sans-serif";
  g.textAlign = "left";
  g.fillText("\u{1F4E2}  Company page \u2014 new post", 24, 30);
  g.fillStyle = "#eef3f8";
  g.fillRect(24, 48, 40, 40);
  g.font = "22px 'Segoe UI Emoji'";
  g.textAlign = "center";
  g.fillStyle = "#3f7fc4";
  g.fillText("\u{1F5DE}\uFE0F", 44, 74);
  g.textAlign = "left";
  g.fillStyle = "#1c2126";
  g.font = "13px Segoe UI, Arial, sans-serif";
  g.fillText("\u201CProud to share our Q3 results at", 76, 62);
  g.fillText("the industry summit this week \u2014", 76, 78);
  g.fillText("read the recap on our blog.\u201D", 76, 94);
  g.fillStyle = "#7a8794";
  g.font = "11px Segoe UI, Arial, sans-serif";
  g.fillText("\u2764\uFE0F 214   \u{1F5E8}\uFE0F 12   \u{1F501} 8  \u2014 Public post", 24, 120);

  // bottom: internal email window
  g.fillStyle = "#ffffff";
  g.fillRect(14, 160, w - 28, 148);
  g.fillStyle = "#4a4f57";
  g.fillRect(14, 160, w - 28, 26);
  g.fillStyle = "#fff";
  g.font = "700 14px Segoe UI, Arial, sans-serif";
  g.fillText("\u2709\uFE0F  Inbox \u2014 1 new message", 24, 178);
  g.fillStyle = "#1c2126";
  g.font = "700 14px Segoe UI, Arial, sans-serif";
  g.fillText("Office summer party \u2014 save the date", 24, 204);
  g.font = "12px Segoe UI, Arial, sans-serif";
  g.fillStyle = "#5a626b";
  g.fillText("From: events@company.internal", 24, 222);
  g.fillStyle = "#333";
  g.fillText("Hi team, this year's summer event will be on the", 24, 246);
  g.fillText("rooftop terrace \u2014 RSVP by Friday so catering can plan.", 24, 262);
  g.fillStyle = "#8f97a1";
  g.font = "11px Segoe UI, Arial, sans-serif";
  g.fillText("Internal \u2014 staff & authorised contractors only", 24, 292);

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

function drawCheck(g: CanvasRenderingContext2D, cx: number, cy: number, r: number, ringColor: string) {
  g.fillStyle = "rgba(30,160,110,0.95)";
  g.beginPath();
  g.arc(cx, cy, r, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = "#0a2a1c";
  g.lineWidth = Math.max(3, r * 0.18);
  g.beginPath();
  g.moveTo(cx - r * 0.5, cy);
  g.lineTo(cx - r * 0.12, cy + r * 0.4);
  g.lineTo(cx + r * 0.55, cy - r * 0.35);
  g.stroke();
  void ringColor;
}

function wrapCentered(
  g: CanvasRenderingContext2D,
  text: string,
  cx: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
) {
  const words = text.split(" ");
  let line = "";
  const lines: string[] = [];
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (g.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((l, i) => g.fillText(l, cx, startY + i * lineHeight));
}

/* ---------------------------------------------------------------------- *
 * Furniture — purely decorative bodies the items above dress up or sit on.
 * ---------------------------------------------------------------------- */

function Desk({ position, rotationDeg }: PrivacyFurnitureItem) {
  const yaw = (rotationDeg ?? 0) * DEG;
  const topW = 1.05;
  const topD = 0.62;
  const topT = 0.03;
  const legH = DESK_H - topT;
  const legs: [number, number][] = [
    [topW / 2 - 0.05, topD / 2 - 0.05],
    [-(topW / 2 - 0.05), topD / 2 - 0.05],
    [topW / 2 - 0.05, -(topD / 2 - 0.05)],
    [-(topW / 2 - 0.05), -(topD / 2 - 0.05)],
  ];
  return (
    <group position={position} rotation={[0, yaw, 0]}>
      <mesh position={[0, DESK_H - topT / 2, 0]} castShadow>
        <boxGeometry args={[topW, topT, topD]} />
        <meshStandardMaterial color="#8a6a45" roughness={0.55} metalness={0.05} />
      </mesh>
      {legs.map(([lx, lz], i) => (
        <mesh key={i} position={[lx, legH / 2, lz]}>
          <boxGeometry args={[0.035, legH, 0.035]} />
          <meshStandardMaterial color="#33383e" roughness={0.6} metalness={0.2} />
        </mesh>
      ))}
    </group>
  );
}

function Shelf({ position, rotationDeg }: PrivacyFurnitureItem) {
  const yaw = (rotationDeg ?? 0) * DEG;
  const w = 0.85;
  const d = 0.26;
  const t = 0.03;
  return (
    <group position={position} rotation={[0, yaw, 0]}>
      <mesh position={[0, SHELF_H - t / 2, 0]}>
        <boxGeometry args={[w, t, d]} />
        <meshStandardMaterial color="#6f5a3f" roughness={0.6} />
      </mesh>
      {[-(w / 2 - 0.05), w / 2 - 0.05].map((bx, i) => (
        <mesh key={i} position={[bx, SHELF_H - t - 0.05, -d / 2 + 0.02]}>
          <boxGeometry args={[0.02, 0.1, 0.02]} />
          <meshStandardMaterial color="#33383e" roughness={0.5} metalness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

/** Sticky note ("click to open") — same drawing convention as DeskPackage's
 *  password note / the retired LoungeLaptop's note. */
function makeLaptopStickyTexture(caption: string): THREE.CanvasTexture {
  const s = 512;
  const cv = document.createElement("canvas");
  cv.width = s;
  cv.height = s;
  const g = cv.getContext("2d")!;
  g.fillStyle = "#ffe873";
  g.fillRect(0, 0, s, s);
  g.fillStyle = "rgba(0,0,0,0.06)";
  g.fillRect(0, 0, s, 40);
  g.fillStyle = "#3a2e0c";
  g.textAlign = "center";
  g.font = "600 44px Comic Sans MS, Segoe Script, cursive";
  const words = caption.split(" ");
  const lines: string[] = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (g.measureText(test).width > s * 0.82 && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  const startY = s / 2 - ((lines.length - 1) * 52) / 2;
  lines.forEach((l, i) => g.fillText(l, s / 2, startY + i * 52));
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/** A laptop, base + tilted-back screen. `position` is the point where its
 *  base sits (usually a desk top, y = DESK_H). The screen shows the shared
 *  two-app preview texture — see `makeLaptopScreenTexture`.
 *
 *  Episode 10 reuses THIS SAME laptop as "your own laptop": when
 *  `stickyCaption` is set, a yellow sticky note is stuck on the bezel
 *  corner and its screen position is reported every frame via
 *  `onStickyPin` — same projection pattern as ReceptionTerminal/
 *  DeskWorkstation, just living on this existing prop instead of a second,
 *  separate laptop mesh. */
function Laptop({
  position,
  rotationDeg,
  stickyCaption,
  onStickyPin,
}: PrivacyFurnitureItem & { onStickyPin?: (pin: LaptopStickyPin) => void }) {
  const { camera } = useThree();
  const yaw = (rotationDeg ?? 0) * DEG;
  const tex = useMemo(() => makeLaptopScreenTexture(), []);
  const stickyTex = useMemo(
    () => (stickyCaption ? makeLaptopStickyTexture(stickyCaption) : null),
    [stickyCaption],
  );
  const stickyAnchor = useRef<THREE.Group>(null);
  const worldPos = useRef(new THREE.Vector3()).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toPin = useRef(new THREE.Vector3()).current;
  const last = useRef<LaptopStickyPin>({ xPct: 0, yPct: 0, visible: false, distance: 999 });
  const onStickyPinRef = useRef(onStickyPin);
  onStickyPinRef.current = onStickyPin;

  const baseW = 0.3;
  const baseD = 0.21;
  const baseH = 0.014;
  const screenW = 0.28;
  const screenH = 0.18;
  const screenT = 0.01;
  const tiltBackDeg = 12;

  useFrame(() => {
    if (!stickyCaption) return;
    const anchor = stickyAnchor.current;
    if (!anchor) return;
    anchor.getWorldPosition(worldPos);
    camera.getWorldDirection(camDir);
    toPin.copy(worldPos).sub(camera.position);
    const distance = toPin.length();
    const inFront = toPin.normalize().dot(camDir) > 0.1;
    const proj = worldPos.clone().project(camera);
    const xPct = (proj.x * 0.5 + 0.5) * 100;
    const yPct = (-proj.y * 0.5 + 0.5) * 100;
    const onScreen = xPct > -5 && xPct < 105 && yPct > -5 && yPct < 105;
    const pin: LaptopStickyPin = { xPct, yPct, visible: inFront && onScreen && distance < 6, distance };
    const p = last.current;
    if (
      p.visible !== pin.visible ||
      Math.abs(p.xPct - pin.xPct) > 0.3 ||
      Math.abs(p.yPct - pin.yPct) > 0.3 ||
      Math.abs(p.distance - pin.distance) > 0.05
    ) {
      last.current = pin;
      onStickyPinRef.current?.(pin);
    }
    if (typeof window !== "undefined") {
      (window as unknown as { __loungePin?: LaptopStickyPin }).__loungePin = pin;
    }
  });

  return (
    <group position={position} rotation={[0, yaw, 0]}>
      <mesh position={[0, baseH / 2, 0]}>
        <boxGeometry args={[baseW, baseH, baseD]} />
        <meshStandardMaterial color="#c7cbd0" metalness={0.35} roughness={0.4} />
      </mesh>
      <mesh position={[0, baseH + 0.0005, 0.01]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[baseW * 0.86, baseD * 0.6]} />
        <meshStandardMaterial color="#1b1d20" roughness={0.7} />
      </mesh>
      <group position={[0, baseH, -baseD / 2]} rotation={[-tiltBackDeg * DEG, 0, 0]}>
        <mesh position={[0, screenH / 2, 0]}>
          <boxGeometry args={[screenW, screenH, screenT]} />
          <meshStandardMaterial color="#d6dadf" metalness={0.3} roughness={0.35} />
        </mesh>
        <mesh position={[0, screenH / 2, screenT / 2 + 0.0006]}>
          <planeGeometry args={[screenW * 0.9, screenH * 0.86]} />
          {/* unlit + toneMapped=false (same as ConferenceTv) -- with the
              ambient/hemi "immersive" lighting profile + ACES tonemapping,
              a lit emissive-map plane clipped this bright UI texture to
              flat white with zero legible detail. A screen should read as
              self-lit regardless of scene exposure. */}
          <meshBasicMaterial map={tex} toneMapped={false} />
        </mesh>
        {stickyCaption && stickyTex && (
          // Placed ON the visible screen area itself (bottom-right corner),
          // not below the hinge on the keyboard deck — the camera waypoint
          // frames the SCREEN, so anything below y=0 (the hinge) projects
          // off the bottom of the viewport and the hotspot is unreachable.
          // Screen visible bounds here are roughly y in [0.013, 0.167],
          // x in [-0.126, 0.126] (90%/86% of screenW/H around center 0.09).
          <group ref={stickyAnchor} position={[0.08, 0.045, screenT / 2 + 0.002]} rotation={[0, 0, -0.08]}>
            <mesh>
              <planeGeometry args={[0.062, 0.062]} />
              <meshStandardMaterial map={stickyTex} roughness={0.9} side={THREE.DoubleSide} />
            </mesh>
          </group>
        )}
      </group>
    </group>
  );
}

function Printer({ position, rotationDeg }: PrivacyFurnitureItem) {
  const yaw = (rotationDeg ?? 0) * DEG;
  const w = 0.34;
  const h = 0.18;
  const d = 0.26;
  return (
    <group position={position} rotation={[0, yaw, 0]}>
      <mesh position={[0, h / 2, 0]}>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color="#e7e9eb" roughness={0.5} />
      </mesh>
      {/* paper output slot, dark inset on the front-top */}
      <mesh position={[0, h - 0.012, d / 2 - 0.01]}>
        <boxGeometry args={[w * 0.66, 0.014, 0.02]} />
        <meshStandardMaterial color="#14161a" roughness={0.8} />
      </mesh>
      {/* control panel accent */}
      <mesh position={[w / 2 - 0.05, h - 0.02, 0]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[0.05, 0.03]} />
        <meshStandardMaterial color="#3f7fc4" emissive="#3f7fc4" emissiveIntensity={0.4} />
      </mesh>
    </group>
  );
}

function FurnitureProp({
  f,
  onLaptopPin,
}: {
  f: PrivacyFurnitureItem;
  onLaptopPin?: (pin: LaptopStickyPin) => void;
}) {
  switch (f.kind) {
    case "desk":
      return <Desk {...f} />;
    case "shelf":
      return <Shelf {...f} />;
    case "laptop":
      return <Laptop {...f} onStickyPin={onLaptopPin} />;
    case "printer":
      return <Printer {...f} />;
    default:
      return null;
  }
}

/* ---------------------------------------------------------------------- *
 * Items — the clickable hotspot anchors. Every style still runs the SAME
 * screen-projection loop (unchanged from the original standee version) so
 * `PrivacyHunt3D`'s DOM pins keep working identically; only the visible
 * geometry at the anchor changes per `renderStyle`.
 * ---------------------------------------------------------------------- */

function ItemProp({
  item,
  found,
  onPin,
}: {
  item: PrivacyRoomItem;
  found: boolean;
  onPin: (pin: PrivacyItemPin) => void;
}) {
  const { camera } = useThree();
  const anchor = useRef<THREE.Group>(null);
  const worldPos = useRef(new THREE.Vector3()).current;
  const camDir = useRef(new THREE.Vector3()).current;
  const toPin = useRef(new THREE.Vector3()).current;
  const last = useRef<PrivacyItemPin>({ id: item.id, xPct: 0, yPct: 0, visible: false, distance: 999 });
  const onPinRef = useRef(onPin);
  onPinRef.current = onPin;

  const style = item.renderStyle ?? "flat";
  const color = LEVEL_COLOR[item.level];

  const paperTex = useMemo(
    () => (style === "flat" && item.size !== "small" ? makePaperTexture(item, found) : null),
    [style, item, found],
  );
  const stickyTex = useMemo(
    () => (style === "flat" && item.size === "small" ? makeStickyTexture(item, found) : null),
    [style, item, found],
  );
  const printoutTex = useMemo(() => (style === "printer_output" ? makePrintoutTexture(item, found) : null), [
    style,
    item,
    found,
  ]);
  const passportTex = useMemo(() => (style === "passport" ? makePassportTexture(found) : null), [style, found]);

  useFrame(() => {
    const a = anchor.current;
    if (!a) return;
    a.getWorldPosition(worldPos);
    camera.getWorldDirection(camDir);
    toPin.copy(worldPos).sub(camera.position);
    const distance = toPin.length();
    const inFront = toPin.normalize().dot(camDir) > 0.1;
    const proj = worldPos.clone().project(camera);
    const xPct = (proj.x * 0.5 + 0.5) * 100;
    const yPct = (-proj.y * 0.5 + 0.5) * 100;
    const onScreen = xPct > -5 && xPct < 105 && yPct > -5 && yPct < 105;
    const pin: PrivacyItemPin = { id: item.id, xPct, yPct, visible: inFront && onScreen && distance < 7, distance };
    const p = last.current;
    if (
      p.visible !== pin.visible ||
      Math.abs(p.xPct - pin.xPct) > 0.3 ||
      Math.abs(p.yPct - pin.yPct) > 0.3 ||
      Math.abs(p.distance - pin.distance) > 0.05
    ) {
      last.current = pin;
      onPinRef.current(pin);
    }
  });

  const yaw = (item.rotationDeg ?? 0) * DEG;

  // "screen" and "usb" items dress up a shared `furniture` laptop and add
  // only a tiny accent of their own (a USB stick) or nothing at all (the
  // laptop's own screen texture already shows the content) — the anchor
  // still needs to exist so a DOM pin renders at the right spot to click.
  if (style === "screen") {
    return (
      <group position={item.position} rotation={[0, yaw, 0]}>
        <group ref={anchor} />
      </group>
    );
  }

  if (style === "usb") {
    // Sized a little larger than a real stick (~50mm) so it reads clearly
    // next to the laptop it's plugged into, rather than disappearing as a
    // barely-visible sliver — matches the "bigger/clearer" note for every
    // item in this room.
    return (
      <group position={item.position} rotation={[0, yaw, 0]}>
        <group ref={anchor} position={[0, 0.018, 0]}>
          <mesh>
            <boxGeometry args={[0.064, 0.022, 0.02]} />
            <meshStandardMaterial color="#d3d7dc" metalness={0.4} roughness={0.4} />
          </mesh>
          <mesh position={[0.047, 0, 0]}>
            <boxGeometry args={[0.022, 0.015, 0.012]} />
            <meshStandardMaterial color="#9aa0a6" metalness={0.8} roughness={0.25} />
          </mesh>
          <mesh position={[-0.018, 0.015, 0]}>
            <sphereGeometry args={[0.0048, 8, 8]} />
            <meshStandardMaterial
              color={found ? "#4ae0a0" : "#6fe0d2"}
              emissive={found ? "#4ae0a0" : "#6fe0d2"}
              emissiveIntensity={0.9}
            />
          </mesh>
        </group>
      </group>
    );
  }

  if (style === "printer_output") {
    const sheetW = 0.25;
    const sheetH = 0.32;
    return (
      <group position={item.position} rotation={[0, yaw, 0]}>
        <group ref={anchor} rotation={[0.55, 0, 0]}>
          <mesh position={[0, 0, sheetH / 2]}>
            <planeGeometry args={[sheetW, sheetH]} />
            <meshStandardMaterial map={printoutTex ?? undefined} roughness={0.9} side={THREE.DoubleSide} />
          </mesh>
        </group>
      </group>
    );
  }

  if (style === "passport") {
    const w = 0.135;
    const d = 0.19;
    const t = 0.02;
    return (
      <group position={item.position} rotation={[0, yaw, 0]}>
        <group ref={anchor} rotation={[-Math.PI / 2, 0, 0]}>
          <mesh position={[0, 0, t / 2]}>
            <boxGeometry args={[w, d, t]} />
            <meshStandardMaterial color="#242868" roughness={0.55} />
          </mesh>
          <mesh position={[0, 0, t + 0.0006]}>
            <planeGeometry args={[w * 0.92, d * 0.92]} />
            <meshStandardMaterial map={passportTex ?? undefined} roughness={0.7} />
          </mesh>
        </group>
      </group>
    );
  }

  // Default: "flat" — a real sheet of paper (or a stiff folder, or a small
  // Post-it) lying FLAT on a desk/shelf surface, never standing upright.
  // Sized a notch above real-world (A4 is ~0.21x0.297) so every prop reads
  // clearly at hunt distance — matches the "bigger/clearer" request for the
  // whole room; the small Post-it was the worst offender (9cm was easy to
  // miss entirely) so it gets the biggest relative bump.
  const small = item.size === "small";
  const cardW = small ? 0.14 : item.folder ? 0.29 : 0.27;
  const cardH = small ? 0.14 : item.folder ? 0.39 : 0.365;
  const thickness = item.folder ? 0.008 : 0.002;
  const tex = small ? stickyTex : paperTex;

  return (
    <group position={item.position} rotation={[0, yaw, 0]}>
      <group ref={anchor} rotation={[-Math.PI / 2, 0, 0]}>
        <mesh position={[0, 0, thickness / 2]}>
          <planeGeometry args={[cardW, cardH]} />
          <meshStandardMaterial map={tex ?? undefined} roughness={0.92} side={THREE.DoubleSide} />
        </mesh>
        {!small && (
          <mesh position={[cardW / 2 - 0.005, 0, thickness + 0.0004]}>
            <planeGeometry args={[0.01, cardH]} />
            <meshStandardMaterial color={color} emissive={color} emissiveIntensity={found ? 0.08 : 0.5} />
          </mesh>
        )}
      </group>
    </group>
  );
}

/**
 * Episode-3 ("office privacy") props: a handful of real 3D furniture
 * pieces (desks, a shelf, a laptop, a printer — `environment.privacyRoom.
 * furniture`) plus up to ~9 clickable items dressed up in a MIX of looks
 * (a laptop screen, a plugged-in USB stick, a printer mid-eject, a closed
 * passport, and a couple of real papers/folders lying flat) instead of one
 * repeated standee card. NOT gated on `scenario.walkable` — like
 * DeskPackage / EntryIntercom these report a screen-projected pin every
 * frame regardless of camera mode, so App.tsx can render a clickable DOM
 * hotspot per item. `foundIds` flips a found item's texture to a green
 * check so the learner can see progress while still walking around in 3D.
 */
export function PrivacyRoomProps({
  items,
  furniture,
  foundIds,
  onPins,
  onLaptopPin,
}: {
  items: PrivacyRoomItem[];
  furniture?: PrivacyFurnitureItem[];
  foundIds: Set<string>;
  onPins: (pins: PrivacyItemPin[]) => void;
  /** Episode 10: reports the (existing, already-rendered) laptop's sticky
   *  note screen position — see `Laptop`'s `stickyCaption`/`onStickyPin`. */
  onLaptopPin?: (pin: LaptopStickyPin) => void;
}) {
  const pinsRef = useRef<Map<string, PrivacyItemPin>>(new Map());
  const onPinsRef = useRef(onPins);
  onPinsRef.current = onPins;

  const handlePin = (pin: PrivacyItemPin) => {
    pinsRef.current.set(pin.id, pin);
    onPinsRef.current(Array.from(pinsRef.current.values()));
  };

  return (
    <group>
      {(furniture ?? []).map((f, i) => (
        <FurnitureProp key={i} f={f} onLaptopPin={onLaptopPin} />
      ))}
      {items.map((item) => (
        <ItemProp key={item.id} item={item} found={foundIds.has(item.id)} onPin={handlePin} />
      ))}
    </group>
  );
}
